import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

import { Money, DateTime } from '../../../components/Formatters';
import { Table, Thead, Tbody, Tr, LinkTr, Th, Td } from '../../../components/Table/Fake';
import { ButtonLink } from '../../../components/Buttons';
import { ListGap } from '../../../components/Paging';

import { getCurrentEvent, getCustomerName, isSession } from '../../sessionHelpers';
import { getListKey } from '../../../helpers/paging';
import { SessionOrGap } from '../types';
import { Session } from '../../../types/checkout';

interface RowProps extends WithTranslation {
    row: SessionOrGap;
    accountId: string;
    fillGap: (id: string) => void;
}

const Row = (
    {
        row,
        accountId,
        fillGap,
        t,
    }: RowProps
) => {
    if (isSession(row)) {
        const transactionLink = row.transaction_id ? (
            <ButtonLink className="small" to={`/${accountId}/payments/transactions/${row.transaction_id}`}>
                {t('payments.sessions.open_transaction')}
            </ButtonLink>
        ) : (
            ''
        );
        const status = t([
            `payments.virtual_terminal.poll_session.events.${getCurrentEvent(row as Session).name}`,
            getCurrentEvent(row as Session).name || '',
        ]);
        return (
            <LinkTr key={row.id} to={`/${accountId}/payments/sessions/${row.id}`} className="stop-borders-before-edges">
                <Td className="first-child">
                    <DateTime isoString={row.created_at || ''} boldTime />
                </Td>
                <Td className="align-right">
                    <Money amount={row.order.amount} currency={row.order.currency} boldCurrency />
                </Td>
                <Td className="ellipsis allow-wrap" title={status}>
                    {status}
                </Td>
                <Td className="collapse-1500 ellipsis">{row.order.store?.id ?? ''}</Td>
                <Td className="collapse-1200 ellipsis">{row.order.merchant_reference}</Td>
                <Td className="collapse-1500 ellipsis">{getCustomerName(row as Session)}</Td>
                <Td className="last-child collapse-1200 no-wrap">{transactionLink}</Td>
            </LinkTr>
        );
    } else {
        return <ListGap fillGap={fillGap} gap={row} label={t('payments.sessions.load_gap')} />;
    }
};

const TranslatedRow = withTranslation()(Row);

export interface SessionTableProps extends WithTranslation {
    sessions: SessionOrGap[];
    accountId: string;
    fillGap: (id: string) => void;
}

const SessionTable = (
    {
        sessions,
        accountId,
        fillGap,
        t,
    }: SessionTableProps
) => {
    return (
        <Table className="no-wrap">
            <Thead>
                <Tr>
                    <Th className="first-child">{t('payments.fields.date')}</Th>
                    <Th className="align-right">{t('payments.fields.amount')}</Th>
                    <Th>{t('payments.fields.status')}</Th>
                    <Th className="collapse-1500">{t('payments.fields.store')}</Th>
                    <Th className="collapse-1200">{t('payments.fields.merchant_reference')}</Th>
                    <Th className="collapse-1500">{t('payments.fields.customer')}</Th>
                    <Th className="last-child collapse-1200">{t('payments.fields.transaction')}</Th>
                </Tr>
            </Thead>
            {sessions.length > 0 && (
                <Tbody>
                    {sessions.map((session) => (
                        <TranslatedRow
                            key={getListKey(session)}
                            row={session}
                            accountId={accountId}
                            fillGap={fillGap}
                        />
                    ))}
                </Tbody>
            )}
        </Table>
    );
};

export default withTranslation()(SessionTable);
